/**
 * OLIMPO BRAND LOADER — global intro animation
 * ─────────────────────────────────────────────────────────────────────────
 * Módulo standalone que gerencia o loader cinematográfico da Olimpo:
 *   1. Verifica se deve tocar (sessionStorage + data-olimpo-loader attribute)
 *   2. Lazy-carrega GSAP só quando necessário (economiza ~110KB em page-views
 *      onde o loader não toca)
 *   3. Amostra pontos dos SVG paths da logo
 *   4. Gera 60 partículas em distribuição polar + converge/desenha/dispersa
 *   5. Dispatcha CustomEvent 'olimpo:loader-done' pra seções ouvirem
 *
 * API pública via body[data-olimpo-loader]:
 *   "once"  (default) — toca na primeira visita da sessão
 *   "force"           — toca SEMPRE (uso em landing pages de campanha)
 *   "off"             — nunca toca
 *
 * CustomEvent disparado no document quando animação completa:
 *   document.addEventListener('olimpo:loader-done', () => { ... });
 *
 * BEST PRACTICES aplicadas:
 *   • IIFE pra não poluir global scope
 *   • Guards em window.__olimpoLoader* pra prevenir dupla execução
 *   • Fallback gracioso se GSAP falhar (timeout 4.5s força exit)
 *   • prefers-reduced-motion respeitado em todos os pontos
 *   • Respeita try/catch em operações SVG (getTotalLength pode falhar)
 *   • No dependency besides GSAP (carregado lazily)
 */

(function () {
  'use strict';

  if (window.__olimpoLoaderBooted) return;
  window.__olimpoLoaderBooted = true;

  var SESSION_KEY = 'olimpo_loader_v1';
  var SAFETY_MS = 4500;
  var GSAP_CDN = 'https://cdn.jsdelivr.net/npm/gsap@3.12.5/dist/gsap.min.js';

  /* ── Session & mode helpers ─────────────────────────────────────────── */
  function getMode() {
    var body = document.body;
    if (!body) return 'once';
    return body.dataset.olimpoLoader || 'once';
  }

  function alreadyPlayed() {
    try {
      return !!sessionStorage.getItem(SESSION_KEY);
    } catch (e) {
      /* sessionStorage bloqueado (privacy modes, iframes cross-origin) → trata como não-tocado */
      return false;
    }
  }

  function markPlayed() {
    try {
      sessionStorage.setItem(SESSION_KEY, '1');
    } catch (e) {
      /* quietly ignore se storage bloqueado */
    }
  }

  function shouldPlay() {
    var mode = getMode();
    if (mode === 'off') return false;
    if (mode === 'force') return true;
    /* default: once per session */
    return !alreadyPlayed();
  }

  function prefersReducedMotion() {
    return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  }

  /* ── GSAP lazy loader ───────────────────────────────────────────────── */
  /* Guard compartilhado com sections/olimpo-novidades.liquid pra evitar dupla
     tag <script>. Ambos checam window.gsap primeiro; browser dedupe via URL
     mesmo se um script já foi enfileirado mas ainda não executou. */
  function loadGsap(onReady) {
    if (window.gsap) return onReady(window.gsap);
    if (window.__olimpoGsapPromise) {
      window.__olimpoGsapPromise.then(onReady);
      return;
    }
    window.__olimpoGsapPromise = new Promise(function (resolve, reject) {
      /* Se alguém mais (section) já adicionou a tag, só observa window.gsap */
      if (window.__olimpoGsapTags) {
        var tries = 0;
        var poll = function () {
          if (window.gsap) return resolve(window.gsap);
          if (tries++ > 150) return reject(new Error('GSAP timeout'));
          setTimeout(poll, 40);
        };
        poll();
        return;
      }
      window.__olimpoGsapTags = true;
      var script = document.createElement('script');
      script.src = GSAP_CDN;
      script.defer = true;
      script.onload = function () {
        if (window.gsap) resolve(window.gsap);
        else reject(new Error('GSAP loaded but global not set'));
      };
      script.onerror = reject;
      document.head.appendChild(script);
    });
    window.__olimpoGsapPromise.then(onReady).catch(function () {
      /* falha ao carregar → skip loader */
      skipLoader();
    });
  }

  /* ── Exit helpers ───────────────────────────────────────────────────── */
  function finalizeExit(loader, cb) {
    loader.classList.add('is-done');
    var done = function () {
      document.documentElement.classList.remove('olimpo-locked');
      if (loader.parentNode) loader.parentNode.removeChild(loader);
      document.dispatchEvent(new CustomEvent('olimpo:loader-done'));
      if (cb) cb();
    };
    /* transitionend é o normal; fallback timeout caso não dispare */
    var fired = false;
    loader.addEventListener(
      'transitionend',
      function () {
        if (fired) return;
        fired = true;
        done();
      },
      { once: true }
    );
    setTimeout(function () {
      if (fired) return;
      fired = true;
      done();
    }, 1200);
  }

  function skipLoader() {
    var loader = document.querySelector('[data-olimpo-loader-root]');
    if (!loader) return;
    document.documentElement.classList.remove('olimpo-locked');
    if (loader.parentNode) loader.parentNode.removeChild(loader);
    document.dispatchEvent(new CustomEvent('olimpo:loader-done'));
  }

  /* ── Particle setup ─────────────────────────────────────────────────── */
  function buildParticles(loader, paths) {
    var particlesGroup = loader.querySelector('[data-olimpo-particles]');
    if (!particlesGroup || !paths.length) return [];

    var anchors = [];
    for (var pi = 0; pi < paths.length; pi++) {
      var p = paths[pi];
      var len = 0;
      try { len = p.getTotalLength(); } catch (e) { continue; }
      if (!len || !isFinite(len) || len < 0.001) continue;
      var pointsPerPath = 10;
      for (var i = 0; i < pointsPerPath; i++) {
        try {
          var pt = p.getPointAtLength((i / pointsPerPath) * len);
          anchors.push({ x: pt.x, y: pt.y });
        } catch (e) {
          /* getPointAtLength pode falhar em paths degenerados */
        }
      }
    }

    if (!anchors.length) return [];

    var VB_W = 29700;
    var VB_H = 21000;
    var VB_CX = VB_W / 2;
    var VB_CY = VB_H / 2;
    var ns = 'http://www.w3.org/2000/svg';
    var count = Math.min(60, anchors.length);
    var particles = [];

    for (var j = 0; j < count; j++) {
      var anchor = anchors[j];
      /* Distribuição polar orgânica — mix de 3 camadas de distância */
      var angle = Math.random() * Math.PI * 2;
      var radiusBase = 8000 + Math.random() * 10000;
      if (Math.random() < 0.15) {
        radiusBase = 1500 + Math.random() * 3000;
      } else if (Math.random() < 0.18) {
        radiusBase = 18000 + Math.random() * 8000;
      }
      var startX = VB_CX + Math.cos(angle) * radiusBase + (Math.random() - 0.5) * 3000;
      var startY = VB_CY + Math.sin(angle) * radiusBase + (Math.random() - 0.5) * 3000;

      var c = document.createElementNS(ns, 'circle');
      var cls = 'olimpo-loader__particle';
      if (j % 3 === 0) cls += ' olimpo-loader__particle--alt';
      c.setAttribute('class', cls);
      c.setAttribute('cx', String(startX));
      c.setAttribute('cy', String(startY));
      c.setAttribute('r', String(90 + Math.random() * 80));
      particlesGroup.appendChild(c);
      particles.push({ el: c, target: anchor });
    }
    return particles;
  }

  /* ── Timeline principal ─────────────────────────────────────────────── */
  function runTimeline(loader, gsap) {
    var paths = loader.querySelectorAll('.olimpo-loader__path');
    var bar = loader.querySelector('.olimpo-loader__bar');
    var particles = buildParticles(loader, paths);

    var safety = setTimeout(function () {
      /* Failsafe: se timeline não completar em 4.5s, força exit */
      paths.forEach(function (p) {
        p.style.strokeDashoffset = '0';
        p.style.fillOpacity = '1';
        p.classList.add('is-filled');
      });
      finalizeExit(loader);
    }, SAFETY_MS);

    var tl = gsap.timeline({
      onComplete: function () {
        clearTimeout(safety);
        markPlayed();
        finalizeExit(loader);
      }
    });

    /* ESTÁGIO 1 — partículas fade-in + convergem pros anchors */
    if (particles.length) {
      var particleEls = particles.map(function (p) { return p.el; });
      tl.to(particleEls, {
        opacity: 1,
        duration: 0.25,
        stagger: { amount: 0.15, from: 'random' },
        ease: 'power1.out'
      }, 0);

      tl.to(particleEls, {
        duration: 0.7,
        ease: 'power3.out',
        stagger: { amount: 0.2, from: 'random' },
        attr: {
          cx: function (i) { return particles[i].target.x; },
          cy: function (i) { return particles[i].target.y; },
          r: function () { return 30 + Math.random() * 40; }
        }
      }, 0.1);
    }

    /* ESTÁGIO 2 — desenha o contorno da logo */
    tl.to(paths, {
      strokeDashoffset: 0,
      duration: 0.7,
      stagger: { amount: 0.3, from: 'start' },
      ease: 'power2.inOut'
    }, 0.5);

    /* ESTÁGIO 3 — fill fade-in + partículas dispersam */
    tl.to(paths, {
      fillOpacity: 1,
      duration: 0.35,
      stagger: { amount: 0.15, from: 'start' },
      ease: 'power2.out',
      onStart: function () {
        paths.forEach(function (p) { p.classList.add('is-filled'); });
      }
    }, 1.25);

    if (particles.length) {
      var dispersionEls = particles.map(function (p) { return p.el; });
      tl.to(dispersionEls, {
        duration: 0.6,
        ease: 'power2.out',
        opacity: 0,
        attr: {
          cx: function (i) {
            var angle = Math.random() * Math.PI * 2;
            return particles[i].target.x + Math.cos(angle) * (2500 + Math.random() * 3500);
          },
          cy: function (i) {
            var angle = Math.random() * Math.PI * 2;
            return particles[i].target.y + Math.sin(angle) * (2500 + Math.random() * 3500);
          },
          r: function () { return 10; }
        }
      }, 1.3);
    }

    /* ESTÁGIO 4 — progress bar */
    tl.to(bar, {
      scaleX: 1,
      duration: 0.4,
      ease: 'power2.inOut'
    }, 1.3);
  }

  /* ── Init ───────────────────────────────────────────────────────────── */
  function init() {
    var loader = document.querySelector('[data-olimpo-loader-root]');
    if (!loader) return;

    if (prefersReducedMotion()) {
      skipLoader();
      return;
    }

    if (!shouldPlay()) {
      skipLoader();
      return;
    }

    document.documentElement.classList.add('olimpo-locked');

    /* Lazy-load GSAP apenas quando a animação vai tocar */
    loadGsap(function (gsap) {
      /* Aguarda próximo frame pra garantir render do SVG antes de medir */
      requestAnimationFrame(function () {
        runTimeline(loader, gsap);
      });
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
